import type { BlogPost } from "./posts";

export const featurePosts: BlogPost[] = [
  {
    slug: "concept-lines-see-how-ideas-connect",
    title: "Concept Lines: See How the Ideas on Your Canvas Connect",
    description:
      "Concept lines highlight ideas shared by text blocks and sticky notes and join them with a colored line. How they work, the limits, and what stays private.",
    date: "2026-09-18",
    readTime: "8 min read",
    category: "Tutorials",
    preserveDate: true,
    content: `
## A Canvas Shows Where You Put Things, Not What They Share

After a planning session or a week of meeting notes, one canvas can hold thirty text blocks and a dozen sticky notes. The layout tells you what you grouped together. It does not tell you that the pricing note in the top-left corner and the interview notes at the bottom are talking about the same thing.

Concept lines do that first reading pass for you. They show which ideas run through more than one block on a canvas, highlight the shared words, and draw a curved line between the blocks, one color per concept.

Concept lines shipped on September 18, 2026, and they are available on every plan.

## Turning Them On

Click **Concepts** at the top right of the canvas. The button turns pink and a legend opens, listing each concept, its color, and how many blocks it links. The setting is remembered on this device, so the lines are there the next time you open a canvas.

Concept lines compare the rich text blocks and sticky notes on the canvas you have open, and update about a third of a second after you stop typing.

## What Counts as a Connection

There are three kinds of match, strongest first.

- **Repeated lines.** A short line of 2 to 12 words, such as a list item or a heading, that appears word for word in another block. Each repeated line links to the exact line it repeats, even when its words are generic. If you copy "follow up with legal" from a meeting block into your to-do block, those two lines are joined.
- **Shared words and phrases.** The same word or two-word phrase in two blocks, ignoring case and plurals, so "customer" and "Customers" match. Filler words such as "the", "need" and "thing" never link, and neither do bare numbers.
- **Related meanings (optional).** With the on-device meaning model downloaded, different words with the same meaning link too: "revenue" and "sales", or "pooled storage" and "storage quota". Repeated lines never match by meaning; they have to be exact.

## Reading the Lines Without the Clutter

The obvious way for this to fail is a canvas covered in colored spaghetti. So at rest, the lines are thin and faint and no words are highlighted.

![A canvas with Concepts on and nothing hovered: faint lines, no highlighted words](/images/blog/concepts-rest.webp "At rest, lines stay faint so the canvas stays readable.")

![The same canvas while hovering a sticky note: its concepts turn bold, with highlighted words and labelled lines](/images/blog/concepts-hover.webp "Hover a block and its concepts come forward.")

Hover over a block, or start editing one, and its concepts come forward: the lines turn bold, the shared words are highlighted in the concept's color, and each concept's name appears on its line. Where lines cross, the labels slide apart, and a label is left off if there is no room for it. Hover a concept in the legend to show only that concept.

## Four Ways to Explore

### Search for a phrase or topic

The legend has a search box. Type a phrase and every block that mentions it is highlighted in yellow and connected by a dark line. With the meaning model on, blocks that say it differently join in too, so a search for "revenue" can pull in a block that only says "sales". Press **Esc** or click **×** to clear it.

![Searching the legend for beta: every block that mentions it is highlighted in yellow and joined by a dark line](/images/blog/concepts-search.webp "Search connects every block that mentions a phrase.")

Where [full-text search](/blog/tutorials/omnicanvas-search-tips) finds notes across your library, this answers a narrower question: where on this canvas did I write about it?

### See where a word connects, then go there

Hover a highlighted word and a small card lists the other blocks it links to, with the words around each match. Each entry has a **Go to** button that pans the canvas to that block. On a large canvas it saves zooming out and hunting.

![Hover card for pricing tiers listing the two other blocks that mention it, each with its excerpt and a Go to button](/images/blog/concepts-card.webp "Hover a highlighted word to see where else it appears.")

### Pin a concept

Click a highlighted word to keep its lines forward after your mouse moves on. Click the word again, click anywhere else, or press **Esc** to let go. **Go to** pins the concept as well, so the word is easy to spot where you land.

### Hide what you don't need right now

Click a concept in the legend to switch its lines off for now; its row greys out. Click it again to bring it back, or click **Show all**. That is different from **×** next to a concept, which stops linking those words on every canvas. Words hidden with **×** come back with **Show again** in settings. If the legend itself is in the way, the chevron next to **Concepts** collapses the list while the lines stay on.

![The Concepts legend with the team concept hidden: its row is greyed out and struck through, with a Show all link](/images/blog/concepts-legend-hidden.webp "Click a row to hide its lines for now.")

## The Meaning Model, and What Stays on Your Device

Matching by meaning needs a language model, and the common way to do that is to send your text to a server. Concept lines do not.

The meaning model is all-MiniLM-L6-v2, a small sentence-embedding model. It is a one-time 23 MB download, and it is only downloaded when you click **Download** in the Concepts legend (or in Concepts settings). The files come from Hugging Face, pinned to one specific version so a change upstream can never trigger an unexpected download, and they are cached in your browser. From then on:

- **Matching runs on your device**, inside the browser. The text of your notes is not sent anywhere.
- **It works offline.**
- **It works on encrypted notes.** Matching happens on the device where your notes are already open, so [client-side encryption](/blog/tutorials/client-side-encryption-private-notes) and concept lines do not get in each other's way.
- **It survives app updates.** The cached model is kept when OmniCanvas updates, so you download it once, not once per release.

The lines and highlights are drawn on top of the canvas and never change your notes. They are not saved, synced, exported, or included in version history. Each device works them out for itself.

## Settings

Click the gear next to **Concepts**. Settings are remembered on this device.

![Concept settings: most concepts to show, link repeated lines, match by meaning, meaning-match sensitivity, and show hidden words again](/images/blog/concepts-settings.webp "The gear next to Concepts opens its settings.")

- **Most concepts to show**: 1 to 8, strongest first. Lower it on a busy canvas.
- **Link repeated lines**: turn this off to link on shared words only.
- **Match by meaning**: turn this off to skip the model even when it is downloaded. You can also download the model from here.
- **Meaning match**: *Strict* links only near-synonyms ("budget" and "spending"). *Balanced*, the default, links the same idea in different words ("revenue" and "sales"). *Loose* also links loosely related ideas ("plan" and "idea").
- **Show again**: restores every word you hid with **×**.

## What Concept Lines Don't Do

The limits, stated plainly:

- **At most 8 concepts per canvas**, one per color, or fewer if you set a lower number. Past eight, thin colored lines stop being easy to tell apart. If a canvas has more shared ideas than that, you see the strongest.
- **One canvas at a time.** Concept lines connect blocks on the canvas you have open. They do not link ideas across different notes. For connections between notes, use wiki links and the [knowledge graph](/blog/tutorials/omnicanvas-knowledge-graph-guide).
- **Text blocks and sticky notes only.** Drawings and images are not part of matching. Words in a block's title can link, but the line starts at the title instead of highlighting the word.
- **Not on phones or narrow windows.** The Concepts button is hidden when the app is narrower than 1,024 pixels, which covers phones, most tablets held upright, and narrow desktop windows.
- **The fewest lines, not every line.** A block links through at most two phrases per concept, and each concept joins its blocks with the fewest lines, nearest blocks first. Three blocks about pricing get two lines, not three.
- **Overlap, not argument.** A line says two blocks share a line, a word or a meaning, not whether they agree. That judgment is still yours.

## Where It Earns Its Place

- **A week of [meeting notes](/blog/productivity/how-to-take-better-meeting-notes) on one canvas.** Repeated lines show which meeting each copied action item came from; shared words show which topics keep coming up.
- **[Research synthesis](/blog/creative/canvas-ux-research-synthesis).** Put interview snippets on sticky notes and see which themes recur across participants before you cluster by hand.
- **Messy brainstorms.** A constraint that keeps surfacing in different corners is usually worth its own block.

For the thinking method behind all of this, with concept lines as one tool among several, see [How to Find Hidden Connections in Your Notes](/blog/knowledge/find-hidden-connections-in-your-notes). For what else shipped the same week, and what we had to fix along the way, read [the build log](/blog/insights/shipped-concept-lines-one-toolbar).

## Frequently Asked Questions

### Do concept lines send my notes to an AI service?
No. Repeated-line and shared-word matching is text comparison inside the app. The optional meaning model runs in your browser. The only network request is the one-time 23 MB model download from Hugging Face, and nothing about your notes or your concepts is uploaded.

### Do I have to download the meaning model?
No. Concept lines match repeated lines and shared words without it. The model only adds matches between different words with the same meaning, such as "revenue" and "sales", and it is downloaded only when you click Download.

### Do concept lines work offline and on encrypted notes?
Yes. Matching, including matching by meaning once the model is downloaded, runs on your device, so it works without a connection and on encrypted notes.

### Are concept lines saved or shared with collaborators?
No. Lines and highlights are drawn on top of the canvas and are never saved, synced, exported or included in version history. Each device works them out for itself from the text on the canvas and its own settings.

### Can I use concept lines on my phone or tablet?
Not on a phone. The Concepts button appears only when the app is at least 1,024 pixels wide, so it shows on desktop and laptop windows and on tablets wide enough to reach that, such as larger tablets held sideways.
`,
  },
  {
    slug: "screenshot-web-pages-into-your-notes",
    title: "Screenshot Any Web Page Into Your Notes — Before It Changes or Disappears",
    description:
      "Web pages rot, mutate and vanish. OmniCanvas captures a full-page screenshot of any URL — plus its searchable text — onto your canvas in one click.",
    date: "2026-06-19",
    readTime: "6 min read",
    category: "Techniques",
    coverImage: null,
    content: `
## Screenshot Any Web Page Into Your Notes

You found the perfect reference — a competitor's pricing page, a dense documentation section, a thread that finally explained the thing, a dashboard that looked exactly right today. You paste the link into your notes and move on.

Three weeks later you click it. The pricing changed. The doc was rewritten. The thread was deleted. The dashboard shows different numbers. The link still resolves, but the *thing you actually wanted* is gone.

A URL is a pointer, not a copy. Pointers break. OmniCanvas fixes this by letting you capture the page itself — a clean, full-page screenshot dropped right onto your infinite canvas, with the page's text saved alongside it so it stays searchable.

## One Click, the Whole Page

Open any note, choose **Clip URL**, paste a link, and pick **Visual** mode. OmniCanvas renders the page and captures it as a *full-page* screenshot — not just the slice that fits in a viewport, but the entire scroll, top to bottom. The image lands as a block on your canvas where you can move it, resize it, and draw on top of it like anything else.

There is nothing to install and nothing to maintain. No browser extension that breaks on the next update. No headless-browser scripts, no Puppeteer setup, no "why did Chromium stop launching in CI" afternoon. You paste a URL; you get the page.

It captures what a real browser sees, so dynamic and JavaScript-heavy pages come through the way they actually look — charts, web apps, and single-page sites included, not a blank shell.

## Searchable, Not Just a Picture

A screenshot you can't search is a dead end six months later. So Visual mode does two things at once: it saves the image **and** extracts the page's text into your note. That means:

- The clip turns up in search when you look for a phrase that was on the page
- You can copy a quote straight out of the text without retyping from a picture
- The screenshot preserves the *layout and design*; the text preserves the *content*

You get the visual record and the searchable record from a single capture.

### Visual vs. Text mode

OmniCanvas gives you two ways to clip, depending on what you're after:

| Mode | What you get | Best for |
|---|---|---|
| **Visual** | Full-page screenshot + searchable text | Design, layout, dashboards, anything you need to *see* exactly as it was |
| **Text** | Clean, readable text only | Articles and docs where you only care about the words |

If in doubt, use Visual — you lose nothing by keeping the picture too.

## Why a Screenshot Beats a Bookmark

Bookmarks and pasted links assume the web holds still. It doesn't. Capturing the page instead of pointing at it matters most when:

- **Pages change.** Pricing, product, and landing pages get edited constantly. A screenshot dated today is evidence of what it said today.
- **Pages disappear.** Deleted posts, expired listings, sunset docs, dead startups. The clip outlives the source.
- **Pages are dynamic.** Dashboards, analytics, and web apps render differently every visit — and won't render at all for someone you share the link with.
- **You're collecting references.** Building a design swipe file, a competitive teardown, or a research board works far better with the actual pages laid out spatially than with a list of blue links.

Because the capture lives on an infinite canvas, you can place a dozen pages side by side, draw arrows between them, and annotate directly on the screenshots. That's the part a folder of bookmarks can never do — see [How to Annotate Anything in OmniCanvas](/blog/techniques/how-to-annotate-anything-in-omnicanvas) for more on marking up captured pages, and [Save and Organize Web Clippings That You'll Actually Revisit](/blog/knowledge/save-organize-web-clippings) for keeping a growing collection usable.

## For Power Users: the Same Capture, Programmatically

Visual capture isn't only a button. The same full-page screenshot-plus-text clip is available through the OmniCanvas API and the MCP integration, using \`mode: "visual"\`. So if you want an agent or a script to archive pages into your notes automatically — every competitor pricing page once a week, say — it's the same capability, no separate screenshot service to wire up.

## Capture It While It's There

The next time you paste a link into your notes "to come back to," ask whether the page will still say the same thing when you return. If it might not, clip it instead. The whole page, searchable, on your canvas — before it changes or disappears.

Web clipping with Visual capture is part of the OmniCanvas Pro plan.
`,
  },
  {
    slug: "whats-new-v0-3-0",
    title: "What's New in OmniCanvas v0.3.0: Calendar Detection, Starred Folders, Offline Mode & More",
    description:
      "OmniCanvas v0.3.0 brings calendar link detection, starred folders, an expanded prompt library, and full offline support. Here is everything that changed.",
    date: "2026-04-05",
    readTime: "5 min read",
    category: "Tutorials",
    content: `
## OmniCanvas v0.3.0

This release focuses on making OmniCanvas faster to use day-to-day. We added four features that our most active users have been requesting: automatic calendar detection in text blocks, starred folders for quick navigation, a more powerful prompt library, and full offline support so you can take notes anywhere. New to OmniCanvas? Start with the [beginner's tutorial](/blog/getting-started/getting-started-with-omnicanvas), and see [setting up cloud sync](/blog/tutorials/omnicanvas-cloud-sync-setup) to keep these changes in sync across devices.

## Calendar Link Detection

Text blocks now automatically detect dates and times in your notes. When OmniCanvas recognizes a pattern like "weekly on Wed at 4:00 PM" or "March 15 at 2pm," it surfaces a small calendar icon next to the detected text. Click it to add the event directly to Google Calendar or Microsoft Outlook — no copy-pasting required.

You can choose your preferred calendar provider in Settings. OmniCanvas supports both Google Calendar and Microsoft Outlook, and switching between them takes one click.

### Supported Patterns

Calendar detection works with a wide range of natural-language date formats:

- Specific dates: "March 15 at 2pm," "2026-04-10 3:00 PM"
- Recurring patterns: "weekly on Wed at 4:00 PM," "every Monday at 9am"
- Relative dates: "tomorrow at noon," "next Friday at 3pm"
- Time ranges: "Thursday 2-4pm," "Jan 20 from 10am to 12pm"

This feature is available on the Pro plan and works inside rich text blocks and sticky notes.

## Starred Folders

You can now star your most important folders so they always appear at the top of the sidebar. Starred folders show up in a dedicated Favorites section above the regular folder tree, giving you one-click access to the projects and areas you use most.

To star a folder, right-click it and select "Star" from the context menu, or click the star icon that appears on hover. Starred folders also sort to the top in the folder tree when browsing normally.

This feature is available on all plans, including Free.

## Expanded Prompt Library

The prompt library is now fully customizable. Instead of being limited to built-in templates, you can create your own AI workflows with completely custom prompt text. Write prompts that match your specific use case — whether that is generating client follow-ups, extracting research themes, or reformatting meeting notes into a specific template.

### What Changed

- **Custom prompts**: Write any prompt you want and save it to your library. Give it a name, assign a category, and run it against any transcript or text block.
- **Hide built-in prompts**: If you only use your own prompts, you can hide the defaults to keep your library clean.
- **Organize by category**: Group prompts into categories like "Meetings," "Research," "Client Work," or anything else that fits your workflow.

Unlike tools like Plaud that offer only fixed templates, OmniCanvas gives you full control over what AI does with your notes.

## Works Offline

OmniCanvas now works as a full progressive web app with offline support. You can create notes, edit text blocks, draw on the canvas, and organize folders without any internet connection.

When your connection returns, OmniCanvas automatically syncs your changes to the cloud. There is no manual step — just open the app and start working, whether you are on a plane, in a subway, or in a building with spotty Wi-Fi.

Core note-taking features work entirely offline. AI features like transcription and prompt execution require a connection, but everything you create offline is preserved and synced seamlessly.

## How to Update

If you are using the web app at app.omnicanvasnotes.com, you already have v0.3.0. Desktop users on macOS will see an update prompt the next time they open OmniCanvas.

We would love to hear what you think of these changes. Reach out on our contact page or join the conversation on social media.
`,
  },
  {
    slug: "omnicanvas-knowledge-graph-guide",
    title: "How to Use the OmniCanvas Knowledge Graph to Discover Hidden Connections",
    description:
      "The knowledge graph in OmniCanvas reveals relationships between your notes that you might never notice otherwise. Learn how to use it for deeper thinking.",
    date: "2026-03-04",
    readTime: "8 min read",
    category: "Tutorials",
    content: `
## What Is the Knowledge Graph?

The knowledge graph is a visual map of all your notes and the connections between them. Instead of browsing notes in a list or grid, the graph view shows every note as a node and draws lines between notes that are related.

It is one of the most powerful features in OmniCanvas because it surfaces relationships you might never find by browsing folders or scrolling through a list.

## How Connections Are Detected

OmniCanvas automatically detects three types of connections between your notes:

### Folder Connections

Notes that share the same folder are connected by dashed lines. This makes sense — if you filed two notes in the same folder, they are probably related. In the graph, folder connections show up as a cluster of nodes grouped around their shared folder.

### Tag Connections

Notes that share one or more tags are connected by dotted blue lines. Tags are a more flexible way to connect notes than folders because a note can have many tags. If you tag three different notes with "product-launch," the graph draws lines between all three, showing you the cluster of ideas around that topic.

### Content Connections

This is where the graph gets really interesting. OmniCanvas analyzes the actual words in your notes — including text on the canvas and inside sticky notes — and draws solid purple lines between notes with similar content. You can adjust the similarity threshold with a slider (1-10) to show more or fewer connections.

Content connections surface relationships you did not explicitly create. You might discover that your notes about a customer interview and your product roadmap brainstorm share significant vocabulary, suggesting a connection worth exploring.

## Navigating the Graph

### Zoom and Pan

Use the scroll wheel to zoom in and out. Click and drag the background to pan around the graph. When you have many notes, zooming out gives you a bird's-eye view of your entire knowledge base.

### Click to Open

Click any node to open that note. This makes the graph a powerful navigation tool — instead of searching or browsing folders, you can visually explore your knowledge and jump to any note.

### Drag to Rearrange

Drag individual nodes to reposition them. This is useful when the automatic layout places two related nodes far apart. Move them closer together to better reflect their relationship.

### Hover to Highlight

Hover over any node to highlight it and all its connections. This instantly shows you every note related to the one you are hovering over, dimming everything else.

## Using the Graph Controls

### Filter by Edge Type

Toggle folder connections, tag connections, and content connections on and off independently. This helps you focus on one type of relationship at a time. For example, turn off folder and tag connections to see only content-based connections — these are often the most surprising and valuable.

### Adjust Content Similarity

The similarity threshold slider controls how similar two notes need to be before the graph draws a content connection. A low setting (1-2) shows only very strong matches. A high setting (8-10) shows looser connections, which can surface unexpected relationships but also creates visual noise.

Start at the middle (5) and adjust based on what you see.

### Filter by Folder

Use the folder filter to show only notes from a specific folder. This is useful when you want to focus on one area of your knowledge base, like a specific project or topic.

### Search Within the Graph

The search bar in graph view highlights matching nodes and their connections. This is great for finding a specific note and then exploring what it connects to.

## Practical Ways to Use the Graph

### Weekly Knowledge Review

Once a week, open the graph view and zoom out to see your entire knowledge base. Look for:

- **Isolated nodes** — Notes with no connections. These might need tags or might belong in a folder.
- **Dense clusters** — Areas with many connections. These are your areas of deep knowledge.
- **Bridge nodes** — Notes that connect two otherwise separate clusters. These are often your most valuable insights because they link different domains.

### Research Synthesis

When researching a topic, create notes for each source and tag them with the topic name. Then open the graph view and filter by that tag. The content connections will show you which sources share ideas, helping you synthesize across sources.

### Creative Exploration

When you feel stuck on a project, open the graph view and find your project notes. Then look at what other notes are connected to them — especially content connections. These unexpected links can spark new ideas.

### Identifying Knowledge Gaps

Zoom out and look at the overall shape of your graph. Areas with few nodes and sparse connections represent topics you have not explored deeply. This can guide your learning and note-taking priorities.

## Tips for a Better Graph

- **Tag consistently.** The more consistently you tag, the more useful tag connections become.
- **Write descriptive notes.** Content connections work better when your notes contain meaningful text, not just bullet points.
- **Don't force connections.** Let the graph reveal connections naturally. Over-tagging or forcing notes into the same folder just to create connections defeats the purpose.
- **Revisit regularly.** The graph becomes more valuable as your note collection grows. A graph with 10 notes is mildly interesting. A graph with 100 notes reveals genuine insights.

The knowledge graph is not just a visualization — it is a thinking tool. It shows you the shape of your knowledge and helps you find connections that linear browsing cannot.

To get the most from it, set up a solid [folders and tags system](/blog/tutorials/omnicanvas-folders-tags-organization) first — and if you are just starting out, the [beginner's tutorial](/blog/getting-started/getting-started-with-omnicanvas) covers the basics.
`,
  },
  {
    slug: "omnicanvas-sticky-notes-canvas-guide",
    title: "Sticky Notes on the Canvas: How to Combine Drawing and Structured Notes",
    description:
      "OmniCanvas lets you place rich-text sticky notes directly on your drawing canvas. Learn how to use this feature for richer, more organized spatial notes.",
    date: "2026-03-11",
    readTime: "7 min read",
    category: "Tutorials",
    content: `
## Drawing Meets Writing

Most notetaking apps force you to choose: write text or draw. OmniCanvas gives you both on the same surface. You can draw freely on the infinite canvas and then drop sticky notes anywhere on top for structured text content.

This combination is more powerful than either alone. Draw a diagram, then annotate it with detailed sticky notes. Sketch a rough idea, then add structured text next to it explaining the details. The canvas handles the visual thinking; the sticky notes handle the verbal thinking.

## Adding a Sticky Note

Click the sticky note button in the note header bar while editing a canvas. A new sticky note appears on your canvas. You can immediately start typing a title and content.

Each sticky note has:

- **A title field** at the top for a quick label
- **A content area** that supports rich text formatting
- **A color** that you can change to categorize or prioritize

## Rich Text in Sticky Notes

Sticky note content supports Markdown formatting, which means you can create structured content inside each note:

- **Bold text** for emphasis
- *Italic text* for softer emphasis
- Bullet lists for organized points
- Numbered lists for sequences
- Blockquotes for callouts or quotes
- Headings for section structure within a single sticky note

This makes sticky notes much more useful than plain text labels. You can write detailed explanations, checklists, or structured arguments inside a single sticky note, all while it lives on your visual canvas.

## Choosing Sticky Note Colors

Six colors are available: yellow, blue, green, pink, purple, and orange. Use color intentionally:

- **Yellow** for general notes and observations
- **Blue** for questions and things to research
- **Green** for decisions and action items
- **Pink** for important warnings or blockers
- **Purple** for ideas and creative thoughts
- **Orange** for references and external links

This is just one suggested system — create whatever color coding works for you. The important thing is consistency so that colors carry meaning at a glance.

## Moving and Resizing

Drag a sticky note by its header to reposition it anywhere on the canvas. Drag the bottom-right corner to resize it. This spatial flexibility lets you arrange sticky notes in whatever layout makes sense for your thinking.

Common layouts:

- **Cluster layout** — Group related sticky notes together near relevant drawings
- **Grid layout** — Arrange sticky notes in a neat grid for comparison
- **Timeline layout** — Line up sticky notes horizontally to represent a sequence
- **Radial layout** — Place a central topic sticky note in the middle with related notes around it

## Practical Use Cases

### Annotated Diagrams

Draw a system architecture, floor plan, or process flow on the canvas. Then add sticky notes next to each component explaining what it does, its status, or questions about it. This creates a rich, self-documenting diagram.

### Meeting Canvases

During a meeting, sketch diagrams on the canvas as you discuss architecture, workflows, or ideas. Drop sticky notes for key decisions, action items, and follow-ups. After the meeting, you have a visual record that is far more useful than a plain text summary.

### Research Boards

Create a canvas for a research topic. Each sticky note represents a source, finding, or question. Arrange them spatially by theme. Draw connections between related findings on the canvas itself. This is a digital version of the classic research corkboard.

### Project Planning

Draw a timeline or flow on the canvas. Add sticky notes for each milestone, task, or deliverable. Color-code by status (green for done, yellow for in progress, pink for blocked). Move sticky notes along the timeline as work progresses.

### Brainstorming Sessions

Start by drawing the problem space on the canvas. Then add sticky notes for each idea, placing them near the part of the problem they address. Cluster similar ideas together. Use colors to categorize ideas by type or feasibility.

## Tips for Effective Sticky Notes

- **Keep titles short.** The title should be scannable when you zoom out — two to four words is ideal.
- **One idea per sticky note.** If you are writing more than a paragraph, consider splitting into multiple sticky notes.
- **Use color consistently.** Pick a color system and stick to it across all your canvases.
- **Combine with drawing.** The real power of sticky notes comes from combining them with canvas drawings. Draw arrows between sticky notes, circle important clusters, or sketch quick diagrams nearby.
- **Review and clean up.** Periodically review your sticky notes. Delete ones that are outdated, merge duplicates, and reorganize clusters.

Sticky notes on the canvas bridge the gap between freeform visual thinking and structured text notes. Use them together for the richest possible spatial notes. Pair them with [Smart Shapes](/blog/tutorials/omnicanvas-smart-shapes-guide) to clean up the diagrams underneath, and if you are new, the [beginner's tutorial](/blog/getting-started/getting-started-with-omnicanvas) shows how sticky notes fit into the bigger picture.
`,
  },
  {
    slug: "omnicanvas-kanban-view-guide",
    title: "How to Use Kanban View in OmniCanvas to Manage Projects Visually",
    description:
      "OmniCanvas includes a Kanban board view that organizes notes into columns by tag or folder. How to use it for lightweight project management.",
    date: "2026-03-18",
    readTime: "7 min read",
    category: "Tutorials",
    content: `
## What Is Kanban View?

Kanban view in OmniCanvas organizes your notes into columns on a board, similar to tools like Trello or Asana. Each column represents a tag or folder, and notes appear as cards within their column.

This transforms your note collection into a visual project management board without needing a separate tool. Your notes are your tasks, and the tags or folders are your workflow stages. New to OmniCanvas? The [beginner's tutorial](/blog/getting-started/getting-started-with-omnicanvas) covers the basics before you build a board.

## Setting Up a Kanban Board

### Using Tags as Columns

The simplest way to create a Kanban workflow is with tags. Create tags for each stage of your workflow:

- **Backlog** — Ideas and tasks you have not started
- **In Progress** — Work you are actively doing
- **Review** — Items waiting for feedback or review
- **Done** — Completed work

When you switch to Kanban view, each tag becomes a column. Notes with that tag appear in the column. Notes without any tags appear in an "Untagged" column.

### Using Folders as Columns

Alternatively, you can organize Kanban columns by folder. Create folders for each project or category, and the Kanban view will display one column per folder. Notes without a folder appear in an "Unfiled" column.

This approach works well when your columns represent different projects rather than workflow stages.

## Working with the Kanban Board

### Drag and Drop

The core interaction is drag-and-drop. Grab a note card and drag it from one column to another. When you move a note between columns, its tag or folder assignment updates automatically.

This makes it easy to move work through your pipeline — drag a note from "Backlog" to "In Progress" when you start working on it, then to "Done" when it is complete.

### Adding Notes

Each column has an "Add note" button at the bottom. Clicking it creates a new note that is automatically tagged or filed with that column's tag or folder. This is the fastest way to add new items to a specific stage.

### Card Information

Each card in the Kanban view shows:

- The note title
- A preview of the note content
- The last modified date
- Any additional tags beyond the column's tag

This gives you enough context to understand what each card is about without opening it.

## Practical Kanban Workflows

### Personal Task Management

Tags: Backlog, This Week, Today, Done

Keep a running backlog of tasks as notes. Each Sunday, drag the most important items into "This Week." Each morning, drag today's priorities into "Today." Move completed items to "Done" throughout the day.

### Content Creation Pipeline

Tags: Ideas, Drafting, Editing, Published

For blog posts, videos, or other content: capture ideas as notes, drag them into "Drafting" when you start writing, move to "Editing" for review, and to "Published" when live.

### Job Search Tracking

Tags: Interested, Applied, Interview, Offer, Rejected

Create a note for each job opportunity with details, contacts, and preparation notes. Drag cards through the stages as your applications progress.

### Bug Tracking

Tags: Reported, Investigating, Fixing, Testing, Resolved

Lightweight bug tracking for solo developers or small teams. Each bug is a note with reproduction steps and details on the canvas. Move through columns as you work on fixes.

### Reading List

Tags: To Read, Reading, Finished

Track books and articles. Add notes as you read with key takeaways and quotes on the canvas. The spatial canvas makes these reading notes much richer than a simple list.

## Tips for Effective Kanban in OmniCanvas

### Keep Columns to 5 or Fewer

Too many columns make the board hard to scan. If you need more granularity, use nested approaches — for example, use tags for workflow stages and folders for projects.

### Set WIP Limits Mentally

Work-in-progress limits are a core Kanban principle. Even though OmniCanvas does not enforce them, keep your "In Progress" column to 3-5 items maximum. If it grows beyond that, finish something before starting something new.

### Use the Canvas for Detail

Each note card in Kanban is also a full canvas. Click to open it and use the infinite canvas and sticky notes for detailed planning, sketches, and research. The Kanban board gives you the overview; the canvas gives you the depth.

### Review the Board Weekly

Set a weekly reminder to review your Kanban board. Archive completed items, reprioritize the backlog, and make sure nothing is stuck in a middle column without attention.

### Combine with Other Views

The Kanban view is just one way to look at your notes. Switch to the graph view to see how your project tasks connect to other knowledge. Switch to [table view](/blog/tutorials/omnicanvas-table-view-power-tips) for sortable, structured data. Each view reveals different aspects of the same notes.

Kanban view turns OmniCanvas from a notetaking app into a lightweight project management tool — without the overhead of a dedicated PM app. Since your project board and your thinking canvas are the same notes, everything stays connected.
`,
  },
  {
    slug: "omnicanvas-table-view-power-tips",
    title: "Table View Power Tips: Sort, Filter, and Manage Notes at Scale",
    description:
      "When you have dozens or hundreds of notes, table view gives you a structured way to sort, filter, and manage them all. Here are the best ways to use it.",
    date: "2026-03-25",
    readTime: "6 min read",
    category: "Tutorials",
    content: `
## When to Use Table View

Grid view is great for visual browsing. Kanban is great for workflows. But when you have a large collection of notes and need to find, sort, or bulk-manage them, table view is the most efficient option.

Table view presents your notes as rows in a structured table with sortable columns. It is the fastest way to get an overview of your entire note collection and take action on multiple notes at once.

## Sorting Your Notes

Table view lets you sort by four criteria:

### Sort by Title

Alphabetical sorting helps when you remember part of a note's title. It also reveals naming patterns — if you see five notes all starting with "Meeting -", you might want to organize them into a dedicated folder.

### Sort by Last Modified

This is the default and usually the most useful sort. Your most recently edited notes appear at the top, making it easy to find what you were working on recently. Switch to ascending order to find old, potentially stale notes that might need archiving.

### Sort by Folder

Group notes by their folder assignment. This lets you quickly see how your notes are distributed across folders and identify notes that might be misfiled.

### Sort by Pinned Status

Pinned notes rise to the top. This is useful when you have a few critical reference notes that you want to access quickly, regardless of other sorting.

Click any column header to sort by that column. Click again to reverse the sort direction.

## Inline Editing

Table view is not just for viewing — you can edit directly in the table:

### Edit Titles

Double-click any note title to rename it inline. This is much faster than opening each note individually when you need to clean up naming conventions across multiple notes.

### Change Folders

Click the folder cell for any note to see a dropdown of your folders. Select a new folder to refile the note instantly. This is the fastest way to reorganize notes into folders.

### Manage Tags

Add or remove tags directly in the table. Each note's tags appear as badges in the tags column. This makes tag cleanup much faster than editing notes one by one.

### Pin and Unpin

Toggle the pin status for any note directly in the table. Pinned notes are marked with an icon.

## Bulk Actions

Table view supports multi-select for bulk operations:

1. Check the checkbox next to each note you want to act on.
2. A bulk action bar appears showing how many notes are selected.
3. Choose an action: **Move to folder** or **Delete to trash**.

This is essential for housekeeping. Select all notes from an old project, move them to an archive folder in one action, or delete outdated notes in bulk.

## Practical Workflows

### Monthly Note Cleanup

Sort by last modified (ascending) to surface your oldest, untouched notes. Review each one — if it is still relevant, update it or refile it. If not, select it for bulk deletion.

### Tagging Backlog

Sort by title and scan for untagged notes. Add tags inline without opening each note. This is the fastest way to catch up on tagging when you have been capturing notes without organizing them.

### Folder Reorganization

Sort by folder to see all notes grouped by their current location. Drag notes between folders or use the inline folder dropdown to refile. This bird's-eye view makes reorganization much more efficient than browsing folder by folder.

### Finding Duplicate Notes

Sort by title to see notes with similar names adjacent to each other. Identify duplicates, open them to compare content, then merge or delete as appropriate.

## Table View vs. Other Views

Use table view when you need to **manage** your notes — sort, filter, bulk edit, reorganize. Switch to grid view when you want to **browse** visually. Switch to [Kanban](/blog/tutorials/omnicanvas-kanban-view-guide) when you want to **track workflows**. Switch to graph view when you want to **explore connections**.

Each view is a different lens on the same collection. The best approach is to switch between them depending on what you are trying to accomplish. Table view is the workhorse for organization and maintenance. New here? The [beginner's tutorial](/blog/getting-started/getting-started-with-omnicanvas) covers the fundamentals first.
`,
  },
  {
    slug: "omnicanvas-smart-shapes-guide",
    title: "Smart Shapes: How AI Shape Recognition Makes Drawing Faster",
    description:
      "OmniCanvas Smart Shapes uses AI to recognize your hand-drawn shapes and clean them up automatically. Learn how to use this feature for cleaner diagrams.",
    date: "2026-04-01",
    readTime: "5 min read",
    category: "Tutorials",
    content: `
## What Are Smart Shapes?

Smart Shapes is an AI-powered feature that recognizes your freehand drawings and converts them into clean geometric shapes. Draw a rough circle and it becomes a perfect circle. Sketch a wobbly rectangle and it snaps into a clean rectangle.

This bridges the gap between the speed of freehand drawing and the polish of precise shapes. You get the natural, intuitive feel of sketching with the clean output of a diagram tool.

## How to Enable Smart Shapes

Toggle Smart Shapes on or off using the button in the note header bar while editing a canvas. When enabled, your hand-drawn shapes are automatically recognized and cleaned up as you draw. When disabled, your strokes stay exactly as you drew them.

You can switch Smart Shapes on and off at any time, even within the same canvas. This lets you use clean shapes for diagrams and freehand strokes for sketches, all on the same surface.

## What Shapes Are Recognized

Smart Shapes recognizes common geometric shapes:

- **Rectangles and squares** — Draw any four-sided closed shape
- **Circles and ovals** — Draw a closed curved shape
- **Lines** — Draw a straight stroke
- **Arrows** — Draw a line with an angled tip
- **Triangles** — Draw a three-sided closed shape
- **Diamonds** — Draw a rotated four-sided shape

The recognition is forgiving — your shapes don't need to be precise. That is the whole point. Draw quickly and roughly, and Smart Shapes cleans up the result.

## Best Use Cases

### Architecture Diagrams

When diagramming software systems, Smart Shapes lets you sketch boxes for services, circles for databases, and arrows for data flow at the speed of thought. The result looks clean enough to share with colleagues.

### Flowcharts

Draw process flowcharts by sketching rectangles for steps, diamonds for decision points, and arrows for flow direction. Smart Shapes produces clean, readable flowcharts from rough sketches.

### Wireframes

Sketch app wireframes with rectangles for screens, smaller rectangles for buttons, and circles for icons. The cleaned-up shapes look polished enough for early-stage design discussions.

### Organizational Charts

Draw boxes for people or teams with lines connecting them. Smart Shapes produces a clean org chart from a quick sketch.

### Mind Maps

Sketch circles or ovals for concepts and lines connecting them. Smart Shapes cleans up the nodes while preserving the organic layout of your mind map.

## Tips for Better Recognition

- **Draw closed shapes.** Make sure the start and end of your shape stroke connect. Open shapes may not be recognized.
- **Draw with intention.** Quick, confident strokes work better than slow, hesitant ones. Trust the recognition.
- **Use reasonable proportions.** Extremely elongated or tiny shapes may not be recognized correctly.
- **Toggle as needed.** If you want a freehand sketch area next to a clean diagram, disable Smart Shapes for the sketch portion.

## When to Turn Smart Shapes Off

Smart Shapes is great for diagrams but not for everything. Turn it off when:

- **Sketching freely.** Creative sketches, doodles, and artistic drawings should stay freehand.
- **Handwriting.** Smart Shapes may try to interpret handwritten text as shapes.
- **Organic drawings.** Anything that should look natural rather than geometric.

The toggle is always one click away, so switch freely between modes as your work requires.

Smart Shapes makes OmniCanvas versatile — it is both a freehand sketchpad and a clean diagramming tool, depending on what you need in the moment. For more ways to combine these shapes into polished diagrams, see [advanced canvas drawing techniques](/blog/tutorials/advanced-canvas-drawing-techniques); if you are just getting started, the [beginner's tutorial](/blog/getting-started/getting-started-with-omnicanvas) is the place to begin.
`,
  },
  {
    slug: "omnicanvas-folders-tags-organization",
    title: "Mastering Folders and Tags in OmniCanvas: An Organization Guide",
    description:
      "Learn how to use OmniCanvas folders and tags together for a flexible, scalable organization system that grows with your note collection.",
    date: "2026-04-08",
    readTime: "8 min read",
    category: "Tutorials",
    content: `
## Two Systems, One Goal

OmniCanvas gives you two complementary ways to organize notes: folders and tags. Used together, they create a flexible system that handles both hierarchical organization and cross-cutting themes.

Understanding when to use each — and how to combine them — is the key to staying organized as your note collection grows.

## Folders: Your Primary Structure

Folders create a hierarchical tree structure. A note lives inside a folder, and folders can contain sub-folders. This mirrors how most people naturally think about organization — by category, project, or area.

### When to Use Folders

Use folders for your primary organizational categories:

- **Projects** — One folder per active project (e.g., "Website Redesign," "Q3 Planning")
- **Areas of life** — Ongoing responsibilities (e.g., "Work," "Personal," "Health")
- **Domains** — Major knowledge areas (e.g., "Engineering," "Design," "Marketing")

OmniCanvas starts you with "Work" and "Personal" folders. Expand from there based on your needs.

### Nesting Folders

Sub-folders add granularity without clutter. For example:

- Work
  - Project Alpha
  - Project Beta
  - Meeting Notes
- Personal
  - Reading Notes
  - Travel
  - Recipes

The folder tree in the sidebar shows your entire hierarchy at a glance. Click the arrow next to any folder to expand or collapse its children.

### Tips for Folders

- **Keep the top level shallow.** Aim for 3-7 top-level folders. Too many creates decision paralysis when filing notes.
- **Create project folders proactively.** When you start a new project, immediately create a folder for it. This reduces the "unfiled notes" problem.
- **Archive completed projects.** When a project ends, you can keep the folder for reference or move notes to a general archive folder to keep your active workspace clean.
- **Use the "Unfiled" section.** Notes without a folder appear in the "Unfiled" section. Check this periodically and file or delete stale notes.

## Tags: Your Cross-Cutting Themes

Tags are labels you attach to notes. Unlike folders, a note can have many tags, and tags do not form a hierarchy. They create horizontal connections across your folder structure.

### When to Use Tags

Use tags for attributes that cut across folders:

- **Status tags** — "in-progress," "review," "done," "blocked"
- **Type tags** — "idea," "reference," "meeting," "decision"
- **Theme tags** — "design," "performance," "onboarding"
- **Priority tags** — "urgent," "important," "someday"

### How Tags Power Other Views

Tags are not just labels — they drive OmniCanvas features:

- **Kanban view** uses tags as columns. Your status tags become a workflow board automatically.
- **[Graph view](/blog/tutorials/omnicanvas-knowledge-graph-guide)** draws connections between notes that share tags. This surfaces related content across different folders.
- **Search** matches against tags, helping you find notes by attribute.
- **Table view** shows tags as a column, letting you sort and filter by tag.

### Tips for Tags

- **Use lowercase and hyphens.** Keep tag names consistent: "in-progress" not "In Progress" or "inProgress."
- **Limit to 15-25 tags total.** Too many tags and you forget which ones exist. If you need more granularity, use folder sub-structure instead.
- **Don't duplicate folders as tags.** If you have a folder called "Marketing," you don't need a tag called "marketing" too.
- **Review tags quarterly.** Delete tags with fewer than 3 notes. Merge similar tags (e.g., "ui" and "design" might be the same thing for your purposes).

## Combining Folders and Tags

The most effective system uses folders for structure and tags for attributes. A note's folder answers "where does this belong?" while its tags answer "what kind of thing is this?"

### Example: Product Team Setup

**Folders:**
- Product Roadmap
- User Research
- Design Specs
- Sprint Notes

**Tags:**
- idea, decision, blocker, shipped
- mobile, web, api
- p0, p1, p2

A note about a mobile design decision lives in the "Design Specs" folder with tags "decision" and "mobile." You can find it by browsing the folder, searching by tag, or spotting it in the knowledge graph connected to other mobile-tagged notes.

### Example: Personal Knowledge Base

**Folders:**
- Work
- Learning
- Personal Projects
- Health

**Tags:**
- idea, reference, journal
- python, design, writing
- review (for notes to revisit)

A Python tutorial note lives in "Learning" with tags "reference" and "python." When you start a Python project later, searching the "python" tag surfaces all your Python-related notes across every folder.

## Common Mistakes

### Over-Organizing

The biggest mistake is spending more time organizing than creating. Your system should be simple enough that filing a note takes less than 5 seconds. If you are agonizing over which folder or tag to use, your system is too complex.

### Folders Only

Using only folders means you miss cross-cutting relationships. Two notes in different folders that share a theme have no visible connection without tags.

### Tags Only

Using only tags means you lose hierarchical structure. Without folders, you have no way to group notes into clear projects or areas.

### Too Many of Either

More than 10 top-level folders or more than 25 tags, and the system becomes hard to maintain. Prune regularly.

The best organization system is the one you actually use. Start simple — a few folders and a handful of tags — and add complexity only when you feel the need for it. If you are new to OmniCanvas, the [beginner's tutorial](/blog/getting-started/getting-started-with-omnicanvas) walks through folders and tags from scratch.
`,
  },
  {
    slug: "omnicanvas-export-pdf-sharing",
    title: "How to Export and Share Your OmniCanvas Notes as PDFs",
    description:
      "Learn how to export your canvas notes as PDFs for sharing, printing, or archiving, with tips for getting the best results.",
    date: "2026-04-15",
    readTime: "5 min read",
    category: "Tutorials",
    content: `
## Why Export to PDF?

Your OmniCanvas notes are powerful thinking tools, but sometimes you need to share them with people who do not use OmniCanvas. PDF export lets you create shareable documents from your spatial notes and canvases. New to the app? The [beginner's tutorial](/blog/getting-started/getting-started-with-omnicanvas) covers the basics first.

Common reasons to export:

- **Sharing meeting notes** with colleagues who were not at the meeting
- **Presenting research** to stakeholders who need a static document
- **Archiving completed projects** in a universal format
- **Printing** canvas diagrams for physical reference
- **Submitting work** for classes or clients

## How to Export

Open the note you want to export and click the export button in the note header. You will see several options to customize your PDF.

### Page Size

Choose between:

- **A4** — Standard international paper size (210 x 297mm). Best for sharing with international colleagues or printing on most printers outside North America.
- **Letter** — US standard paper size (8.5 x 11 inches). Best for printing in the US and Canada.
- **Auto** — Automatically sizes the page to fit your canvas content. Best for digital sharing where print size does not matter.

### Include Title

Toggle whether the note's title appears at the top of the PDF. Enable this when sharing with others so they know what they are looking at. Disable it for clean prints of diagrams or artwork.

### Background

Choose between:

- **White** — Clean white background. Best for printing and most sharing scenarios.
- **Transparent** — No background. Useful if you want to layer the exported content on top of other documents or slides.

### Exporting

Click the export button and your browser's print dialog will appear. From there, you can save as PDF or send directly to a printer.

## Tips for Better Exports

### Canvas Layout Matters

Before exporting, arrange your canvas content so it reads well on a single page or a sequence of pages. Zoom in on the area you want to export — the export captures what is visible on your canvas.

### Text Size

Text that looks good on screen might be too small when printed. If you plan to export, use slightly larger text sizes on your canvas elements and sticky notes.

### Use Auto Size for Complex Canvases

If your canvas has content spread across a wide area, use the Auto page size. This prevents your content from being cropped or shrunk to fit a standard page size.

### Export Sections Separately

For large canvases, consider exporting different areas separately rather than trying to fit everything on one page. Zoom into a section, export it, then zoom to the next section and export again.

### Sticky Notes in Exports

Sticky notes on your canvas are included in the PDF export. Make sure their content is complete and readable before exporting, since the recipient cannot interact with them.

## Sharing Without Export

PDF export is not the only way to share. If your recipients also use OmniCanvas, cloud sync means you can both access the same notes by logging into the same account. This keeps the notes interactive rather than static.

For quick sharing with anyone, export to PDF. For ongoing collaboration, use [cloud sync](/blog/tutorials/omnicanvas-cloud-sync-setup).
`,
  },
  {
    slug: "omnicanvas-evernote-import-migration",
    title: "How to Migrate from Evernote to OmniCanvas",
    description:
      "Step-by-step guide for importing your Evernote notebooks into OmniCanvas, including how to export from Evernote and what to expect during the import.",
    date: "2026-04-22",
    readTime: "6 min read",
    category: "Tutorials",
    content: `
## Why Switch from Evernote?

Evernote has been a staple of notetaking for years, but many users are looking for alternatives. Whether you want the spatial canvas approach, are frustrated with Evernote's pricing changes, or simply want a fresh start, OmniCanvas makes it easy to bring your existing notes with you.

The import feature reads Evernote's ENEX export format and creates OmniCanvas notes from your Evernote content, preserving titles, text content, and dates. If you are brand new to OmniCanvas, start with the [beginner's tutorial](/blog/getting-started/getting-started-with-omnicanvas).

## Step 1: Export from Evernote

First, you need to export your notes from Evernote in ENEX format.

### On Evernote Desktop (Mac or Windows)

1. Open Evernote and navigate to the notebook you want to export.
2. Select all notes in the notebook (Cmd+A on Mac, Ctrl+A on Windows).
3. Right-click and choose "Export Notes."
4. Select "ENEX format (.enex)" as the export format.
5. Choose a save location and click "Export."

Repeat for each notebook you want to migrate.

### Exporting Multiple Notebooks

Evernote exports one notebook at a time. If you have many notebooks, export each one as a separate ENEX file. OmniCanvas creates a folder for each imported file, so this maps naturally to your existing notebook structure.

## Step 2: Import into OmniCanvas

1. Open OmniCanvas and click the Settings button in the sidebar.
2. In the Migration and Import section, you will see the Evernote import option.
3. Click "Import" and select your ENEX file.
4. OmniCanvas will process the file and create notes from your Evernote content.

### What Happens During Import

- OmniCanvas creates an "Evernote Imports" folder automatically.
- Inside that, it creates a sub-folder named after your ENEX file (which usually matches your Evernote notebook name).
- Each Evernote note becomes an OmniCanvas note with the same title.
- Text content from your Evernote notes is preserved.
- The original last-modified dates are preserved so you can see when notes were last updated.

### Large Files

If you have a large ENEX file with hundreds of notes, the import may take a moment. OmniCanvas processes the file in the background using a web worker, so the app stays responsive during import. A progress indicator shows you how the import is progressing.

## Step 3: Organize Your Imported Notes

After import, your notes are organized in the "Evernote Imports" folder structure. From here, you can:

### Reorganize into Your New Structure

Move notes from the import folders into your preferred OmniCanvas folder structure. You might want to consolidate multiple Evernote notebooks into fewer OmniCanvas folders, or you might keep the same structure. Our guide to [mastering folders and tags](/blog/tutorials/omnicanvas-folders-tags-organization) can help you design that structure.

### Add Tags

Evernote tags are not imported automatically. Review your imported notes and add OmniCanvas tags where appropriate. This is a good opportunity to set up a fresh, consistent tagging system.

### Enrich with Canvas Content

Your Evernote notes were linear text. Now that they are in OmniCanvas, you can enhance them with canvas drawings, sticky notes, and spatial organization. Consider which notes would benefit from a visual treatment.

### Archive Old Notes

You probably don't need all your Evernote notes in your active workspace. Move old, rarely-referenced notes to an archive folder. Keep your active workspace focused on current and relevant content.

## What Gets Imported (and What Does Not)

### Imported

- Note titles
- Text content
- Last modified dates
- Notebook structure (as folders)

### Not Imported

- Images and attachments (Evernote stores these in a proprietary format)
- Evernote tags (you will need to re-tag in OmniCanvas)
- Shared notebook settings
- Reminders and tasks

## Tips for a Smooth Migration

- **Don't import everything.** If you have years of Evernote notes, many are probably outdated. Export and import only the notebooks you actively use.
- **Start fresh alongside.** Use OmniCanvas for new notes immediately. Import old Evernote notes gradually as you need them, rather than trying to migrate everything upfront.
- **Keep Evernote temporarily.** Don't delete your Evernote account until you are confident everything you need has been migrated and is working well in OmniCanvas.
- **Use the graph view after import.** Once your notes are imported, check the knowledge graph to see how they connect. You might discover relationships between old notes that you never noticed in Evernote's linear notebook structure.
`,
  },
  {
    slug: "omnicanvas-search-tips",
    title: "Search Like a Pro: Finding Anything in OmniCanvas Instantly",
    description:
      "OmniCanvas search goes beyond titles — it searches your canvas content, sticky notes, tags, and folders. Learn how to find any note in seconds.",
    date: "2026-04-29",
    readTime: "5 min read",
    category: "Tutorials",
    content: `
## Beyond Title Search

Most notetaking apps only search note titles or body text. OmniCanvas searches across multiple fields simultaneously, which means you can find notes even if you don't remember the title.

When you type in the search bar, OmniCanvas looks through:

- **Note titles** — The name you gave your note
- **Tags** — Any tags attached to the note
- **Folder names** — The folder (and parent folders) containing the note
- **Canvas text** — Any text you typed directly on the canvas using the text tool
- **Sticky note content** — Text inside sticky notes on the canvas

This multi-field search means you can find a note by remembering any detail about it — its name, a tag you added, the folder it is in, or even a word you wrote on the canvas. New to OmniCanvas? The [beginner's tutorial](/blog/getting-started/getting-started-with-omnicanvas) covers the essentials first.

## Using Search Effectively

### The Keyboard Shortcut

Press Cmd+K (or Ctrl+K on Windows) to instantly focus the search bar from anywhere in the app. This is the fastest way to start searching. Type your query and press Enter.

### Search by What You Remember

You do not need to remember the exact title. A few strategies:

- **Remember a tag?** Search for the tag name. Notes tagged with "project-x" will appear when you search "project-x."
- **Remember the folder?** Search for the folder name. All notes in that folder will match.
- **Remember a word on the canvas?** Search for that word. If you wrote "architecture" in a text element on the canvas, searching "architecture" will find that note.
- **Remember a sticky note?** Search for words from the sticky note content. Even content buried in a sticky note on the canvas is searchable.

### Search in Graph View

Search works in the graph view too. When you search in graph view, matching nodes are highlighted and their connections become visible while non-matching nodes dim. This is a powerful way to explore what else is connected to the notes that match your search.

## Quick Search Patterns

### Find Recent Work

If you remember working on something but not the name, sort by last modified instead of searching. Switch to table view and sort by the "Last Modified" column to see your most recently edited notes at the top.

### Find Notes by Type

If you use consistent tags for note types (like "meeting," "idea," or "reference"), searching for the type tag instantly filters to all notes of that type.

### Find Notes Across Folders

Search ignores folder boundaries. If you are looking for all notes related to "onboarding" regardless of which project folder they are in, just search "onboarding" and results from every folder will appear.

## Building Search-Friendly Notes

A few habits make your notes easier to find later:

### Use Descriptive Titles

"Meeting Notes 3/15" is hard to find later. "Product Review - Q1 Metrics Discussion" is much more searchable. Include the topic in the title, not just the date or type.

### Tag Consistently

Tags are powerful search shortcuts. A consistent tagging habit means you always have multiple ways to find any note.

### Write Key Terms on the Canvas

If your canvas is primarily visual (drawings and shapes), add a text element with key terms related to the content. This makes the visual canvas searchable by text.

### Use Sticky Notes for Context

Add a sticky note to each canvas summarizing what the canvas contains. This "meta" sticky note gives the search engine more text to match against.

## Search and the Knowledge Graph

Search and the [knowledge graph](/blog/tutorials/omnicanvas-knowledge-graph-guide) are complementary tools for finding information:

- **Use search when you know what you are looking for.** You have a specific term, tag, or topic in mind.
- **Use the graph when you want to explore.** You are not sure what you are looking for but want to browse connections and discover related notes.

Together, they ensure you can always find your notes — whether through targeted retrieval or visual exploration.
`,
  },
  {
    slug: "omnicanvas-cloud-sync-setup",
    title: "Setting Up Cloud Sync in OmniCanvas: Access Your Notes Everywhere",
    description:
      "OmniCanvas cloud sync keeps your notes in sync across the desktop app and web. Learn how to set it up and get the most out of cross-device access.",
    date: "2026-05-06",
    readTime: "6 min read",
    category: "Tutorials",
    content: `
## Why Cloud Sync?

OmniCanvas [works great offline](/blog/tutorials/whats-new-v0-3-0) — your notes are saved locally and available without an internet connection. But cloud sync adds an important layer: access your notes from any device and keep them backed up on the server.

With sync enabled, notes you create on your Mac desktop app are available on the web app from any browser. Changes you make on one device appear on the other after syncing.

## Setting Up Your Account

### Creating an Account

1. Open OmniCanvas (desktop app or web app).
2. On the auth screen, enter your email and password.
3. Click "Sign Up" to create your account.
4. You are now logged in and sync is available.

If you have been using OmniCanvas without an account, your existing local notes are preserved. After signing up, use the Sync button to push them to the cloud.

### Skipping Account Creation

You can skip account creation and use OmniCanvas entirely offline. Click "Skip" on the auth screen to go straight to the app. You can always create an account later from the Settings panel.

## How Syncing Works

### Push and Pull

OmniCanvas sync uses a push/pull model:

- **Push** sends your local notes to the server. Any notes you created or modified locally are uploaded.
- **Pull** downloads notes from the server. Any notes that were modified on another device are downloaded to your current device.

When you sync, both push and pull happen together.

### Automatic Sync on Login

When you log in, OmniCanvas automatically syncs — pulling down any notes from the server that you do not have locally, and pushing up any local notes that the server does not have.

### Manual Sync

After the initial sync, use the "Sync Now" button in Settings to trigger a manual sync at any time. This is useful after making changes that you want to appear on another device immediately.

### Conflict Resolution

If the same note was modified on two different devices, the version with the most recent modification date wins. The other version is overwritten. For this reason, avoid editing the same note on two devices simultaneously without syncing in between.

## Accessing Notes on Different Devices

### Desktop App (Mac)

The desktop app is the primary OmniCanvas experience. It runs as a native macOS app with full offline support. Notes are saved to local storage and synced to the cloud when you have an internet connection.

### Web App

Open the web app in any browser to access your synced notes. This works on any device — Windows, Linux, tablets, or even your phone. The web app has the same features as the desktop app.

### Workflow: Desktop + Web

A common workflow:

1. Use the desktop app on your Mac for daily notetaking — it is fast and works offline.
2. When you are away from your Mac, open the web app on your phone or another computer to reference or quickly edit notes.
3. Sync before switching devices to make sure everything is up to date.

## Settings and Account Management

The Settings panel (accessible from the sidebar) shows your sync status:

- **Your email** — Confirms which account you are logged into.
- **Sync status** — Shows when the last sync occurred.
- **Sync Now button** — Triggers a manual sync.
- **Sign Out** — Logs you out of your account. Your local notes remain on the device.

## Tips for Reliable Sync

### Sync Before Switching Devices

Before closing OmniCanvas on one device and opening it on another, hit "Sync Now" to make sure your latest changes are uploaded. Then sync again on the new device to pull them down.

### Sync After Important Changes

After creating important notes or making significant edits, sync immediately. This ensures your work is backed up on the server even if something happens to your local device.

### Don't Edit the Same Note on Two Devices

If you edit a note on your Mac and then edit the same note on the web before syncing, one version will overwrite the other. Sync between edits to avoid losing changes.

### Check Your Connection

Sync requires an internet connection. If you are working offline, your changes are saved locally and will sync the next time you are online and trigger a sync.

Cloud sync turns OmniCanvas from a single-device app into a cross-device knowledge system. Set it up once, sync regularly, and your notes are always available wherever you need them. If you are new, the [beginner's tutorial](/blog/getting-started/getting-started-with-omnicanvas) covers the essentials.
`,
  },
  {
    slug: "client-side-encryption-private-notes",
    title: "Client-Side Encryption in OmniCanvas: How to Keep Your Notes Truly Private",
    description:
      "Per-note client-side encryption with AES-256-GCM. Notes are encrypted in your browser before they leave your device — not even we can read them.",
    date: "2026-03-07",
    readTime: "7 min read",
    category: "Tutorials",
    content: `
## Why Note Encryption Matters

Most notetaking apps store your data in plaintext on their servers. That means the company, its employees, and anyone who breaches the server can read your notes. For personal journals, health records, financial planning, legal notes, or sensitive work documents, that is a real risk.

OmniCanvas takes a different approach. With client-side encryption, your notes are encrypted in your browser before they ever leave your device. The server only stores ciphertext — scrambled data that is meaningless without your password. Not even we can read your encrypted notes.

## How It Works

### The Encryption Standard

OmniCanvas uses **AES-256-GCM**, an industry-standard authenticated encryption algorithm used by banks, governments, and security professionals worldwide.

Here is what happens under the hood when you encrypt a note:

1. **Key derivation** — Your password is run through PBKDF2-SHA256 with 600,000 iterations, producing a 256-bit encryption key. This process is intentionally slow to make brute-force attacks impractical.
2. **Encryption** — Your note's content (canvas data, sticky notes, audio recordings, and OCR text) is encrypted using AES-256-GCM with a random initialization vector (IV).
3. **Storage** — Only the ciphertext, salt, and IV are stored. Your password and encryption key are never sent to the server or stored anywhere on disk.

### Zero-Knowledge Architecture

This is a zero-knowledge system. The encryption and decryption happen entirely in your browser using the Web Crypto API. The server never sees your password, your encryption key, or your plaintext data.

Even if someone gained full access to the OmniCanvas database, your encrypted notes would be unreadable without your password.

## How to Encrypt a Note

Encrypting a note takes about five seconds:

1. Find the note in your sidebar or note list
2. Open the note's context menu (click the three-dot menu or right-click)
3. Click **Encrypt**
4. Enter a password (minimum 4 characters) and confirm it
5. Click **Encrypt Note**

That is it. Your note is now encrypted. You will see a lock icon next to the note title in the sidebar and note list.

### What Gets Encrypted

When you encrypt a note, the following data is encrypted:

- **Canvas data** — All drawings, shapes, text, and Excalidraw elements
- **Sticky notes** — All sticky note content and positions
- **Audio recordings** — Any voice memos attached to the note
- **OCR text** — Any handwriting recognition results

The note's **title, tags, folder assignments, and timestamps** remain unencrypted so you can still search, sort, and organize your notes without unlocking them.

## Opening an Encrypted Note

When you click an encrypted note, OmniCanvas prompts you for the password. Enter it, and the note decrypts instantly in your browser.

To save you from re-entering the password every time, OmniCanvas caches the derived encryption key in memory for your current session. This means you can open, edit, and save the note freely until you close or reload the app. The cached key is never written to disk or sent anywhere.

## Editing Encrypted Notes

Once you unlock an encrypted note, you can edit it normally. Every time you save, OmniCanvas automatically re-encrypts the updated content with a fresh IV before storing it. You do not need to do anything special — encryption is transparent once the note is unlocked.

## Removing Encryption

If you decide a note no longer needs encryption:

1. Open the note's context menu
2. Click **Remove Encryption**
3. Enter the password
4. The note is decrypted and stored as plaintext going forward

## Important: No Password Recovery

This is the most important thing to understand about client-side encryption: **if you forget your password, your note cannot be recovered.** There is no "forgot password" option. There is no backdoor. There is no support team that can decrypt it for you.

This is by design. If we could recover your data, it would mean we had access to your encryption key — which would defeat the purpose of zero-knowledge encryption.

### Tips for Managing Encryption Passwords

- **Use a password manager** — Store your note encryption passwords in a dedicated password manager like 1Password, Bitwarden, or Apple Keychain
- **Use strong, unique passwords** — Each encrypted note can have its own password, so use different passwords for different sensitivity levels
- **Do not use your account password** — Your OmniCanvas login password and your note encryption passwords should be different

## Encrypted Notes and Collaboration

Encrypted notes cannot be shared or used in real-time collaboration sessions. This is intentional — sharing an encrypted note would require sharing the encryption key, which would compromise the zero-knowledge model.

If you need to collaborate on a note, remove encryption first, collaborate, then re-encrypt when you are done.

## When to Use Encryption

Encryption adds a small step (entering a password) to your workflow, so it makes sense to use it selectively for notes that genuinely need protection:

- **Personal journals and diaries** — Keep private thoughts private
- **Financial planning** — Budget spreadsheets, investment notes, tax planning
- **Health and medical notes** — Symptoms, medications, doctor visit notes
- **Legal documents** — Contracts, case notes, privileged communications
- **Work confidential** — Trade secrets, unreleased product plans, HR notes
- **Passwords and credentials** — Though a dedicated password manager is better for this

For everyday notes like meeting agendas or grocery lists, plaintext is fine.

## Technical Details

| Parameter | Value |
|-----------|-------|
| Algorithm | AES-256-GCM |
| Key derivation | PBKDF2-SHA256 |
| PBKDF2 iterations | 600,000 |
| Salt length | 128 bits (random per note) |
| IV length | 96 bits (random per save) |
| Implementation | Web Crypto API (browser-native) |

The entire encryption implementation is open and auditable in the client-side code. No proprietary or server-side cryptography is involved.

## Getting Started

Client-side encryption is available now for all OmniCanvas users — free and paid. Open any note, click the menu, and choose Encrypt to get started. Your most sensitive notes deserve zero-knowledge protection. Because only ciphertext leaves your device, your encrypted notes stay private even with [cloud sync](/blog/tutorials/omnicanvas-cloud-sync-setup) enabled; new users can start with the [beginner's tutorial](/blog/getting-started/getting-started-with-omnicanvas).
`,
  },
];
